import { EventSubscriber, ReplicaType, rewriteAuthzOriginator, VerdantError, } from '@verdant-web/common';
import { HANDLE_MESSAGE, PresenceManager } from './PresenceManager.js';
import { FileSync } from './FileSync.js';
import { PushPullSync } from './PushPullSync.js';
import { ServerSyncEndpointProvider, } from './ServerSyncEndpointProvider.js';
import { WebSocketSync } from './WebSocketSync.js';
import { attemptToRegisterBackgroundSync } from './background.js';
export class NoSync extends EventSubscriber {
    send() { }
    async start() { }
    stop() { }
    ignoreIncoming() { }
    reconnect() { }
    setMode() { }
    setPullInterval() { }
    constructor(ctx) {
        super();
        this.mode = 'pull';
        this.hasSynced = false;
        this.destroy = () => { };
        this.isConnected = false;
        this.status = 'paused';
        this.pullInterval = 0;
        this.uploadFile = async () => {
            return {
                success: false,
                retry: false,
            };
        };
        this.getFile = async () => {
            return {
                success: false,
                error: 'Sync is not active',
            };
        };
        this.syncOnce = async () => { };
        this.presence = new PresenceManager({
            initialPresence: null,
            defaultProfile: null,
            ctx,
        });
    }
}
export class ServerSync extends EventSubscriber {
    constructor({ authEndpoint, fetchAuth, initialPresence, automaticTransportSelection = true, autoStart, initialTransport, pullInterval, presenceUpdateBatchTimeout, defaultProfile, useBroadcastChannel, onOutgoingMessage, EXPERIMENTAL_backgroundSync, }, { ctx, onData, }) {
        super();
        this.broadcastChannel = null;
        this._activelySyncing = false;
        this._hasSynced = false;
        this.handleBroadcastChannelMessage = (event) => {
            if (event.data.type === 'sync') {
                this.handleMessage(event.data.message, { source: 'broadcastChannel' });
            }
        };
        this.handleMessage = async (message, { source } = {
            source: 'network',
        }) => {
            var _a;
            // cancel if client is shutting down
            if (this.ctx.closing)
                return;
            // TODO: move this into metadata
            if (message.type === 'op-re' || message.type === 'sync-resp') {
                for (const op of message.operations) {
                    this.ctx.time.update(op.timestamp);
                }
            }
            this.ctx.log('debug', 'sync message', JSON.stringify(message, null, 2));
            switch (message.type) {
                case 'op-re':
                    await this.onData({
                        operations: message.operations,
                        baselines: message.baselines,
                    });
                    if (message.globalAckTimestamp) {
                        await this.ctx.meta.setGlobalAck(message.globalAckTimestamp);
                    }
                    break;
                case 'global-ack':
                    await this.ctx.meta.setGlobalAck(message.timestamp);
                    break;
                case 'sync-resp':
                    this._activelySyncing = true;
                    this.emit('syncingChange', true);
                    await this.onData({
                        operations: message.operations,
                        baselines: message.baselines,
                        reset: message.overwriteLocalData,
                    });
                    if (message.globalAckTimestamp) {
                        await this.ctx.meta.setGlobalAck(message.globalAckTimestamp);
                    }
                    await this.ctx.meta.updateLastSynced(message.ackedTimestamp);
                    this._activelySyncing = false;
                    this.emit('syncingChange', false);
                    this._hasSynced = true;
                    this.emit('synced');
                    break;
                case 'need-since':
                    this.emit('serverReset', message.since);
                    this.ctx.files.onServerReset(message.since);
                    this.activeSync.send(await this.ctx.meta.messageCreator.createSyncStep1(message.since));
                    break;
                case 'server-ack':
                    await this.ctx.meta.updateLastSynced(message.timestamp);
            }
            // avoid rebroadcasting messages
            if (source === 'network') {
                (_a = this.broadcastChannel) === null || _a === void 0 ? void 0 : _a.postMessage({
                    type: 'sync',
                    message,
                });
            }
            // update presence if necessary
            this.presence[HANDLE_MESSAGE](await this.ctx.meta.getLocalReplica(), message);
        };
        this.handleOnlineChange = async (online) => {
            this.emit('onlineChange', online);
            // if online, attempt to upload any unsynced files.
            if (online) {
                const unsyncedFiles = await this.ctx.files.listUnsynced();
                const results = await Promise.allSettled(unsyncedFiles.map((file) => this.fileSync.uploadFile(file)));
                if (results.some((r) => r.status === 'rejected')) {
                    this.ctx.log('error', 'Failed to upload unsynced files', results
                        .filter((r) => r.status === 'rejected')
                        .map((r) => r.reason));
                }
            }
        };
        this.handlePresenceUpdate = async (data) => {
            this.send(await this.ctx.meta.messageCreator.createPresenceUpdate(data));
        };
        this.setMode = (transport) => {
            if (transport === 'realtime' && !this.canDoRealtime) {
                throw new Error(`Cannot switch to realtime mode, because the current auth token does not allow it`);
            }
            let newSync;
            if (transport === 'realtime') {
                newSync = this.webSocketSync;
            }
            else {
                newSync = this.pushPullSync;
            }
            if (newSync === this.activeSync)
                return;
            this.ctx.log('debug', 'switching to', transport, 'mode');
            // transfer state to new sync
            if (this.activeSync.status === 'active') {
                newSync.start();
            }
            this.activeSync.stop();
            this.activeSync = newSync;
        };
        this.setPullInterval = (interval) => {
            this.pushPullSync.setInterval(interval);
        };
        this.send = async (message) => {
            var _a, _b;
            if (this.activeSync.status === 'active') {
                // before sync, replace 'originator' authz subjects
                // with token userId. This is the easiest place to
                // do this and allows the rest of the system to be
                // ambivalent about user identity when assigning
                // authorization for the current user.
                const userId = (_a = this.endpointProvider.tokenInfo) === null || _a === void 0 ? void 0 : _a.userId;
                if (!userId) {
                    throw new VerdantError(VerdantError.Code.Unexpected, undefined, 'Active sync has invalid token info');
                }
                if (message.type === 'sync' || message.type === 'op') {
                    rewriteAuthzOriginator(message, userId);
                }
                await this.activeSync.send(message);
                (_b = this.onOutgoingMessage) === null || _b === void 0 ? void 0 : _b.call(this, message);
            }
        };
        this.uploadFile = async (info) => {
            var _a;
            this.ctx.log('info', 'Uploading file', {
                name: info.name,
                type: info.type,
                id: info.id,
                size: (_a = info.file) === null || _a === void 0 ? void 0 : _a.size,
            });
            if (this.activeSync.status === 'active') {
                return this.fileSync.uploadFile(info);
            }
            else {
                return {
                    success: false,
                    retry: false,
                    error: 'Sync is not active',
                };
            }
        };
        this.getFile = async (id) => {
            // TODO: should this error? or just try anyway?
            if (this.activeSync.status === 'active') {
                return this.fileSync.getFile(id);
            }
            else {
                // wait for sync to start, up to 5 seconds
                await this.getSyncStartPromise();
                if (this.activeSync.status === 'paused') {
                    throw new VerdantError(VerdantError.Code.Offline, undefined, 'Sync is not active');
                }
                return this.fileSync.getFile(id);
            }
        };
        this.getSyncStartPromise = (timeout = 5000) => {
            return new Promise((resolve, reject) => {
                const timeoutHandle = setTimeout(() => {
                    reject(new Error('Sync did not start in time'));
                    unsubscribe();
                }, timeout);
                const unsubscribe = this.subscribe('onlineChange', (online) => {
                    if (online) {
                        clearTimeout(timeoutHandle);
                        unsubscribe();
                        resolve();
                    }
                });
            });
        };
        this.start = () => {
            return this.activeSync.start();
        };
        this.stop = () => {
            return this.activeSync.stop();
        };
        this.destroy = () => {
            this.dispose();
            this.webSocketSync.destroy();
            this.pushPullSync.destroy();
        };
        this.reconnect = () => {
            return this.activeSync.reconnect();
        };
        /**
         * Runs one isolated sync cycle over HTTP. This is useful for
         * syncing via a periodic background job in a service worker,
         * which keeps a client up to date while the app isn't open.
         */
        this.syncOnce = () => {
            return this.pushPullSync.syncOnce();
        };
        this.onData = onData;
        this.ctx = ctx;
        this.onOutgoingMessage = onOutgoingMessage;
        this.presence = new PresenceManager({
            initialPresence,
            defaultProfile,
            updateBatchTimeout: presenceUpdateBatchTimeout,
            ctx,
        });
        this.endpointProvider = new ServerSyncEndpointProvider({
            authEndpoint,
            fetchAuth,
        }, ctx);
        this.webSocketSync = new WebSocketSync({
            endpointProvider: this.endpointProvider,
            presence: this.presence,
            ctx,
        });
        this.pushPullSync = new PushPullSync({
            endpointProvider: this.endpointProvider,
            presence: this.presence,
            interval: pullInterval,
            ctx,
        });
        this.fileSync = new FileSync({
            endpointProvider: this.endpointProvider,
            ctx,
        });
        if (useBroadcastChannel && 'BroadcastChannel' in window) {
            this.broadcastChannel = new BroadcastChannel(`verdant-${ctx.namespace}`);
            this.broadcastChannel.addEventListener('message', this.handleBroadcastChannelMessage);
        }
        if (initialTransport === 'realtime') {
            this.activeSync = this.webSocketSync;
        }
        else {
            this.activeSync = this.pushPullSync;
        }
        this.presence.subscribe('update', this.handlePresenceUpdate);
        ctx.meta.events.subscribe('syncMessage', this.send);
        this.webSocketSync.subscribe('message', this.handleMessage);
        this.webSocketSync.subscribe('onlineChange', this.handleOnlineChange);
        this.pushPullSync.subscribe('message', this.handleMessage);
        this.pushPullSync.subscribe('onlineChange', this.handleOnlineChange);
        if (automaticTransportSelection && this.canDoRealtime) {
            // automatically shift between transport modes depending
            // on whether any peers are present (matching view if
            // applicable)
            const decideIfUpgrade = () => {
                if (switchoverTimeout) {
                    clearTimeout(switchoverTimeout);
                }
                const hasPeers = this.presence.getViewPeers().length > 0;
                const shouldUpgrade = hasPeers ||
                    (automaticTransportSelection !== 'peers-only' &&
                        this.presence.selfReplicaIds.size > 1);
                if (shouldUpgrade && this.mode === 'pull') {
                    this.setMode('realtime');
                }
                else if (!shouldUpgrade && this.mode === 'realtime') {
                    // wait 1 second then switch to pull mode if still empty
                    switchoverTimeout = setTimeout(() => {
                        if (this.presence.getViewPeers().length === 0) {
                            this.setMode('pull');
                        }
                    }, 1000);
                }
            };
            let switchoverTimeout;
            this.presence.subscribe('peersChanged', decideIfUpgrade);
            if (automaticTransportSelection !== 'peers-only') {
                this.presence.subscribe('selfChanged', decideIfUpgrade);
            }
        }
        if (autoStart) {
            this.start();
        }
        if (EXPERIMENTAL_backgroundSync) {
            attemptToRegisterBackgroundSync();
        }
    }
    get canDoRealtime() {
        return (this.endpointProvider.type === ReplicaType.Realtime ||
            this.endpointProvider.type === ReplicaType.PassiveRealtime ||
            this.endpointProvider.type === ReplicaType.ReadOnlyRealtime);
    }
    get syncing() {
        return this._activelySyncing;
    }
    get hasSynced() {
        return this._hasSynced;
    }
    get pullInterval() {
        return this.pushPullSync.interval;
    }
    ignoreIncoming() {
        this.activeSync.ignoreIncoming();
    }
    get isConnected() {
        return this.activeSync.isConnected;
    }
    get status() {
        return this.activeSync.status;
    }
    get mode() {
        return this.activeSync.mode;
    }
}
//# sourceMappingURL=Sync.js.map